import React from 'react'
import qs from 'query-string'
import accounting from 'accounting'
import jwtDec from 'jwt-decode'
import dlv from 'dlv'
import constants from './constants'
import striptags from 'striptags'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import ja from 'date-fns/locale/ja'
import Resizer from 'react-image-file-resizer'
import { getRoute, getDynamicPath, Routes } from './routes'

export const getInitialPropsMock = mock => {
  return (() => {
    return require(`./mocks/initial-props/${mock}.json`)
  })()
}

export const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
)

export const PaymentTypes = {
  CREDIT_CARD: 'credit_card',
  COD: 'cod',
  PAY_LATER: 'paylater',
  ZERO_YEN: 'zeroyen',
  PAY_PAY: 'paypay',
  DBARAI: 'dbarai'
}

export const DeliveryDateMode = {
  NO_DATE: 'no_date',
  DATE_SELECTION: 'date_selection'
}

export const ShippingTypes = {
  FSS: 'FSS',
  FSS_SIS_LOCKER: 'FSS/SiS/Locker',
  CSTORE: 'CSTORE',
  YAMATO_QURE: 'YAMATO_QURE',
  YAMATO: 'Yamato'
}

export const PinTypes = {
  TEXT: 'text',
  VOICE: 'voice'
}

export const PurchaseLimitationErrorCodes = {
  REGISTERED_DEVICE_AMOUNT: 'pl_01',
  TRANSACTION_AMOUNT: 'pl_02',
  ISOLATED_ITEM: 'pl_03',
  CUSTOMER_OWNING_AMOUNT: 'pl_04',
  GLOBAL_AMOUNT: 'pl_05',
  OVER_PURCHASE_LIMIT_INCOMPLETE_ORDER: 'pl_06',
  REACHED_LIMIT_PER_DEVICE_INCOMPLETE_ORDER: 'pl_07'
}

export const shippingMethodIsPickup = shippingMethod => {
  return (
    shippingMethod.shipping === ShippingTypes.FSS ||
    shippingMethod.shipping === ShippingTypes.FSS_SIS_LOCKER ||
    shippingMethod.shipping === ShippingTypes.CSTORE
  )
}

export const TierStatus = {
  GOLD: 'gold',
  NO_TIER: 'notier',
  PLATINIUM: 'platinum',
  SILVER: 'silver'
}

export const getErrorStatus = (error = {}) => {
  return error.status ? error.status : error.message
}

export const makeSSRError = (err = {}) => {
  return {
    message: err.message,
    status: err.status,
    data: err.data,
    isRequestError: err.isRequestError,
    isTranslationError: err.isTranslation,
    requestOpts: err.requestOpts,
    stack: err.stack
  }
}

export const makeClientSideError = (err = {}) => {
  // we might want to log this client side error somewhere
  return makeSSRError(err)
}

export const getBaseUrl = () => {
  return constants.SITE_BASE_URL
}

const DEFAULT_CURRENCY = '¥'

export const formatCurrency = (
  value,
  currency = DEFAULT_CURRENCY,
  decimal = 0
) => {
  if (value !== null && !isNaN(value)) {
    if (currency.symbol && currency.decimals) {
      const s = currency.symbol
      const d = parseInt(currency.decimals, 10) || decimal
      return accounting.formatMoney(value, s, d)
    }
    return accounting.formatMoney(value, currency, decimal)
  }
}

export const formatDate = dob => {
  return format(
    parse(dob, 'yyyy-MM-dd', new Date()),
    constants.DEFAULT_DATE_FORMAT,
    {
      locale: ja
    }
  )
}

export const formatProductUrl = (product, option, isDefaultVariant) => {
  return getRoute(
    getDynamicPath(Routes.products, product.id),
    false,
    false,
    option && !isDefaultVariant ? [`variantid=${option.variantId}`] : null
  )
}

export const buildFormForVeritransCCPayment = gw => {
  function buildInput(name, value) {
    const hiddenField = document.createElement('input')
    hiddenField.setAttribute('name', name)
    hiddenField.setAttribute('value', value)
    hiddenField.setAttribute('type', 'hidden')
    return hiddenField
  }
  const form = document.createElement('form')
  form.setAttribute('method', 'post')
  form.setAttribute('action', gw.action)
  form.appendChild(buildInput('MERCHANT_ID', gw.MERCHANT_ID))
  form.appendChild(buildInput('ORDER_ID', gw.ORDER_ID))
  form.appendChild(
    buildInput('BROWSER_ENCRYPTION_KEY', gw.BROWSER_ENCRYPTION_KEY)
  )
  return form
}

export function getMock(obj, path) {
  return Object.values(obj).some(val => val === true) ? path : undefined
}

const EMPTY_STRING = ''
export function isEmptyString(s) {
  return !(s && s.trim && s.trim() !== EMPTY_STRING)
}

export function newRelicReport(err) {
  if (canUseDOM && window.newrelic && window.newrelic.noticeError) {
    window.newrelic.noticeError(err)
  }
}

export function newSSRErrorRelicReport(err) {
  const errorObj = new Error('SSRError')
  errorObj.stack = err.stack
  let errorRequestURLPathname = null
  try {
    if (err.requestOpts && err.requestOpts.url) {
      const url = new URL(err.requestOpts.url)
      errorRequestURLPathname = url.pathname
    }
  } catch (err) {
    errorRequestURLPathname = null
  }
  const errorDetails = {
    message: err.message,
    isRequestError: err.isRequestError,
    isTranslationError: err.isTranslation,
    errorStatus: JSON.stringify(err.status),
    component: err.component,
    errorDataMessage: extractErrorMessage(err),
    errorRequestURLPathname
  }
  return { errorObj, errorDetails }
}

export function updateCartCounter(amount) {
  canUseDOM &&
    window.dispatchEvent(
      new window.CustomEvent('onReactCartCounterUpdate', { detail: { amount } })
    )
}

export function resetCartCount() {
  updateCartCounter(0)
}

export function extractErrorMessage(err, defaultValue = '') {
  if (
    err.data &&
    Array.isArray(err.data) &&
    err.data.length > 0 &&
    err.data[0].message
  ) {
    return err.data[0].message
  }
  if (err.data) {
    const errObj =
      err.data[Object.keys(err.data).find(key => key.toLowerCase() === 'error')]
    if (errObj && errObj.message) return errObj.message
  }
  if (err.data && err.data.message && typeof err.data.message === 'string') {
    return err.data.message
  }
  if (err.data && typeof err.data === 'string') {
    return err.data
  }
  return defaultValue
}

export function extractErrorCode(err) {
  if (
    err.data &&
    Array.isArray(err.data) &&
    err.data.length > 0 &&
    err.data[0].code
  ) {
    return err.data[0].code
  }
  return null
}

export function extractErrorType(err) {
  if (
    err.data &&
    Array.isArray(err.data) &&
    err.data.length > 0 &&
    err.data[0].type
  ) {
    return err.data[0].type
  }
  return null
}

export function JWTDecode(token) {
  try {
    return jwtDec(token)
  } catch (e) {
    return null
  }
}

export function get(obj, path, defaultValue) {
  if (defaultValue) return dlv(obj, path, defaultValue)
  const value = dlv(obj, path)
  if (value === undefined)
    throw new Error(`The key '${path}' is not defined in the theme object`)
  return value
}

export function isEmpty(value) {
  return value === undefined || value === '' || value == null
}

export function mapProductInfoForDisplay(
  requestedProduct,
  variantIdsToDisplay,
  variantid
) {
  if (requestedProduct) {
    let optionIndex
    let selectedOption
    let options = requestedProduct.options || []
    if (variantIdsToDisplay && variantIdsToDisplay.length > 0) {
      // variant list to be displayed
      options = options.filter(option =>
        variantIdsToDisplay.includes(option.variant_id.toString())
      )
      if (variantid) {
        optionIndex = options.indexOf(
          options.find(
            option => option.variant_id.toString() === variantid.toString()
          )
        )
      }

      selectedOption =
        optionIndex !== undefined ? (optionIndex === -1 ? 0 : optionIndex) : 0
      if (options.length === 0) {
        // validate if variants list has wrong variant ids and show variants from ecom
        options = requestedProduct.options || []
        selectedOption = undefined
      }
    }
    const maxQty = requestedProduct.max_qty > 0 ? requestedProduct.max_qty : 6
    const minQty = requestedProduct.min_qty || 1
    const promoBanner =
      requestedProduct.promotional_banners &&
      requestedProduct.promotional_banners[0]
    const videos = requestedProduct.videos
    const prodImages = requestedProduct.images
    const availableQuantities = Array.from(Array(maxQty).keys())
      .map(q => (q + 1 >= minQty ? q + 1 : null))
      .filter(Boolean)
    return {
      id: requestedProduct.id,
      product: {
        id: requestedProduct.id,
        name: requestedProduct.name,
        category: requestedProduct.main_category_name,
        attributes: requestedProduct.attributes,
        maxQty,
        minQty,
        availableQuantities,
        promoBanner,
        bv: requestedProduct.bv,
        videos,
        prodImages,
        isRequiredDeviceRegistration:
          requestedProduct.is_required_device_registration,
        seoTitle: requestedProduct.seo_title,
        seoDescription: requestedProduct.seo_description,
        family: requestedProduct.family,
        subscriptionLinkText: requestedProduct.subscription_link_text,
        subscriptionLinkUrl: requestedProduct.subscription_link_url,
        preorderable: requestedProduct.preorderable,
        outOfStockAction: requestedProduct.out_of_stock_actions,
        options: options.map(option => ({
          optionId: Number(option.option_id),
          variantId: Number(option.variant_id),
          color: option.hex_color,
          price: parseFloat(option.price),
          listPrice: parseFloat(option.list_price),
          stock: 0,
          inStock: true,
          optionName: option.option_name,
          description: option.full_description,
          shortDescription: option.short_description || null,
          variantName: option.variant_name,
          shortVariantName: option.short_variant_name,
          displayPostButton: option.display_post_button === 'True' || false,
          images:
            option.images &&
            option.images.map(image => ({
              path: image.image_path || null,
              alt: getImageAltValue(image.alt, option.variant_name)
            })),
          productCode: option.product_code,
          badge: option.badge,
          bv_id: (option.bv && option.bv.bv_id) || null
        }))
      },
      selectedOptionIndex:
        selectedOption !== undefined
          ? selectedOption
          : requestedProduct.selected_variant >= 0
          ? requestedProduct.selected_variant
          : 0,
      quantity: minQty
    }
  }
}

export function mapProductListForDisplay(products, ignoreStocks = true) {
  return (products || [])
    .map(product => {
      return {
        id: product.id || null,
        name: product.name || null,
        category: product.main_category_name,
        selectedOptionIndex:
          product.selected_variant >= 0 ? product.selected_variant : 0,
        images:
          product.images &&
          product.images.map(image => ({
            path: image.image_path || null
          })),
        bv: product.bv,
        isRequiredDeviceRegistration: product.is_required_device_registration,
        outOfStockAction: product.out_of_stock_actions,
        options:
          product.options &&
          product.options.map(option => ({
            optionId: Number(option.option_id),
            variantId: Number(option.variant_id),
            hex: option.hex_color,
            price: parseFloat(option.price),
            listPrice: parseFloat(option.list_price),
            stock: ignoreStocks ? 0 : parseInt(option.stock, 10),
            description: option.full_description || null,
            shortVariantName: option.short_variant_name,
            optionName: option.option_name,
            variantName: option.variant_name,
            images:
              option.images &&
              option.images.map(image => ({
                path: image.image_path || null,
                alt: getImageAltValue(image.alt, product.name)
              })),
            badge: option.badge,
            bv_id: (option.bv && option.bv.bv_id) || null,
            discount: option.discount || 0
          }))
      }
    })
    .filter(el => el.options && el.options.length > 0)
}

export function mapBundleListForDisplay(bundles, ignoreStocks = true) {
  return (
    bundles &&
    bundles.map(chain => {
      const productsTotalStockAvailable =
        chain.products &&
        chain.products.every(
          el =>
            parseInt(
              !ignoreStocks && el.total_stock ? el.total_stock : '0',
              10
            ) > 0
        )

      const allvariants = chain.products
        ? chain.products.map(product => {
            return {
              id: product.id,
              color: product.hex_color,
              colorName: product.color_name,
              image: product.image_path,
              description: product.full_description,
              inStock:
                productsTotalStockAvailable > 0 &&
                parseInt(product.stock, 10) > 0,
              selectedVariant: product.selected_variant || 0,
              options:
                product.options &&
                product.options.map(option => {
                  return {
                    id: option.option_id,
                    optionName: option.option_name,
                    variantId: option.variant_id,
                    variantName: option.variant_name,
                    shortVariantName: option.short_variant_name,
                    color: option.hex_color,
                    image: option.images &&
                      option.images.length > 0 &&
                      option.images[0] && {
                        path: option.images[0].image_path || null,
                        alt: getImageAltValue(option.images[0].alt, chain.name)
                      },
                    inStock:
                      productsTotalStockAvailable > 0 &&
                      parseInt(option.stock, 10) > 0,
                    price: parseFloat(option.price),
                    listPrice:
                      option.list_price !== null
                        ? parseFloat(option.list_price)
                        : undefined,
                    discount: option.discount || 0
                  }
                })
            }
          })
        : []

      const selectedProducts = allvariants.reduce(
        (selectedProducts, product, index) => {
          selectedProducts[index] = product.selectedVariant
          return selectedProducts
        },
        {}
      )
      const bundlePrice =
        allvariants &&
        allvariants.length > 0 &&
        Object.keys(selectedProducts)
          .map(key => {
            return allvariants[key].options
              ? allvariants[key].options[selectedProducts[key]].price
              : 0
          })
          .reduce((a, b) => a + b, 0)
      const bundleListPrice =
        allvariants &&
        allvariants.length > 0 &&
        Object.keys(selectedProducts)
          .map(key => {
            const price = allvariants[key].options
              ? allvariants[key].options[selectedProducts[key]].price
              : 0
            const discount = allvariants[key].options
              ? allvariants[key].options[selectedProducts[key]].discount
              : 0
            return price + discount
          })
          .reduce((a, b) => a + b, 0)

      const bundle = {
        id: chain.chain_id,
        name: chain.name,
        currency: chain.currency,
        price: bundlePrice,
        listPrice: bundleListPrice,
        image: {
          path: chain.img_path || null,
          alt: getImageAltValue(chain.alt, chain.name)
        },
        description: chain.description,
        fullDescription: chain.full_description_line,
        badge: chain.badge,
        products: allvariants.slice(0, Math.min(allvariants.length, 3)),
        selectedProductIndex: chain.product_id,
        categoryId: chain.category_id,
        category: chain.main_category_name
      }

      return {
        ...bundle
      }
    })
  )
}

export function fillProductsWithExtraData(products, resultProducts) {
  const optionStockMap = {}
  const productTotalStockMap = {}
  const optionPriceMap = {}
  const optionListPriceMap = {}
  const optionDiscountMap = {}
  const optionAlreadyUsedPromotionsMap = {}
  const stockProducts = resultProducts || []

  stockProducts.forEach(requestedProduct => {
    const requestedProductOptions = requestedProduct.options || []
    productTotalStockMap[requestedProduct.id] = requestedProduct.total_stock
    requestedProductOptions.forEach(option => {
      optionStockMap[option.variant_id] = parseInt(option.stock, 10)
      optionPriceMap[option.variant_id] = parseFloat(option.price)
      optionListPriceMap[option.variant_id] = parseFloat(option.list_price)
      optionDiscountMap[option.variant_id] = parseFloat(option.discount) || 0
      optionAlreadyUsedPromotionsMap[option.variant_id] =
        (option.already_used_promotions &&
          option.already_used_promotions.map(promotion => ({
            promotionId: promotion.promotion_id,
            name: promotion.name,
            rejectionMessage: promotion.rejection_message
          }))) ||
        []
    })
  })

  const noStockProducts = products || []
  const updatedProducts = noStockProducts.map(product => ({
    ...product,
    inStock: productTotalStockMap[product.id] > 0,
    options: (product.options || []).map(option => ({
      ...option,
      price: optionPriceMap[option.variantId],
      listPrice: optionListPriceMap[option.variantId],
      discount: optionDiscountMap[option.variantId],
      stock: optionStockMap[option.variantId],
      inStock: optionStockMap[option.variantId] > 0,
      alreadyUsedPromotions: optionAlreadyUsedPromotionsMap[option.variantId]
    }))
  }))

  return updatedProducts
}

export function fillBundlesWithExtraData(bundles, resultBundles) {
  const noStockBundles = bundles || []
  const stockBundles = resultBundles || []
  const findCurrentResultBundle = bundle =>
    stockBundles.filter(resultBundle => resultBundle.chain_id === bundle.id)[0]

  const updatedBundles = noStockBundles.map(bundle => {
    const products =
      noStockBundles.length > 0 && stockBundles.length > 0
        ? fillProductsWithExtraData(
            bundle.products,
            findCurrentResultBundle(bundle).products
          )
        : []

    const selectedProducts = products.reduce(
      (selectedProducts, product, index) => {
        selectedProducts[index] = product.selectedVariant
        return selectedProducts
      },
      {}
    )

    const bundlePrice = Object.keys(selectedProducts)
      .map(key => {
        return products[key].options[selectedProducts[key]].price
      })
      .reduce((a, b) => a + b, 0)
    const bundleListPrice = Object.keys(selectedProducts)
      .map(key => {
        const variant = products[key].options[selectedProducts[key]]
        return variant.price + variant.discount
      })
      .reduce((a, b) => a + b, 0)

    return {
      ...bundle,
      products: products,
      price: bundlePrice,
      listPrice: bundleListPrice
    }
  })

  return updatedBundles || []
}

export function buildCartData(res) {
  const discount =
    res.discount > 0
      ? res.discount
      : res.subtotal_discount > 0
      ? res.subtotal_discount
      : 0
  const products = res.products.map(el => ({
    id: el.product_id,
    name: el.variant || el.product,
    price: formatCurrency(el.price, el.currency),
    basePrice: formatCurrency(el.base_price, el.currency),
    category: el.main_category_name,
    amount: el.amount,
    maxQty: el.max_qty,
    hint: null,
    surveyDevice: el.survey_device,
    image:
      el.main_pair &&
      el.main_pair.icon &&
      !Array.isArray(el.main_pair.icon) &&
      typeof el.main_pair.icon === 'object'
        ? el.main_pair.icon.image_path
        : null,
    chainId: el.chain_id ? el.chain_id : null,
    chainName: el.chain_name ? el.chain_name : null,
    chainPrice: formatCurrency(el.chain_price, el.currency),
    isSample: !!el.is_sample,
    isLogistics: !!el.is_logistics,
    isSampleConfirmed: !!el.is_sample_confirmed,
    family: el.family,
    displayDiscountValue: el.display_discount_value || false,
    productCode: el.is_logistics
      ? el.product_code
      : el.options &&
        el.options[el.selected_variant >= 0 ? el.selected_variant : 0] &&
        String(
          el.options[el.selected_variant >= 0 ? el.selected_variant : 0]
            .product_code
        ),
    options:
      el.product_options &&
      Object.keys(el.product_options).map(key => ({
        optionId: Number(key),
        variantId: Number(el.product_options[key])
      }))
  }))
  products.sort((a, b) =>
    a && a.family && b && b.family ? a.family.priority - b.family.priority : 0
  )
  return {
    products,
    amount: res.amount || 0,
    subtotal: formatCurrency(res.subtotal, res.currency),
    total: formatCurrency(res.total, res.currency),
    hasDiscount: discount > 0,
    discount: formatCurrency(discount, res.currency),
    discountFlat: discount,
    delivery: formatCurrency(res.shipping_cost, res.currency),
    deliveryFlat: res.shipping_cost,
    currency: res.currency,
    hideCoupon: !res.can_apply_coupon || false,
    hasBundle: !!res.has_bundles,
    editableShipping: res.editable_shipping_address,
    hasTobacco: res.has_tobacco,
    preorder: res.preorder,
    // X = user never created a TVA (redirect to TVA screen)
    // A = user created a TVA and it is approved (ask for password and proceeed with the order
    // D = user created a TVA and it is declined (redirect to TVA screen)
    // N = not determined (ask for password and proceeed with the order
    tvaStatus: res.tva_status || 'N',
    promotions:
      res.applied_promotions &&
      Object.keys(res.applied_promotions)
        .map(el => {
          return {
            name: res.applied_promotions[el] && res.applied_promotions[el].name,
            id: el,
            priority:
              parseInt(
                res.applied_promotions[el] &&
                  res.applied_promotions[el].priority
              ) || 0,
            confirmed:
              res.applied_promotions[el] && res.applied_promotions[el].confirmed
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.priority - b.priority),
    coupons: res.coupons ? Object.keys(res.coupons) : [],
    surchargeValue: res.payment_surcharge
      ? formatCurrency(res.payment_surcharge, res.currency)
      : null,
    surchargeTitle: res.payment_surcharge_title,
    paymentId: res.payment_id,
    eligibleCoupons:
      res.eligible_coupons &&
      Object.keys(res.eligible_coupons).map(el => {
        return {
          name: res.eligible_coupons[el].name,
          code: res.eligible_coupons[el].coupon,
          expiration: res.eligible_coupons[el].expiration_date,
          description: res.eligible_coupons[el].short_description
        }
      }),
    hasFullDiscountCoupon:
      res.eligible_coupons &&
      !!(
        Object.keys(res.eligible_coupons).filter(
          el => res.eligible_coupons[el].full_discount
        ) || []
      ).length,
    sagawaEligible: res.sagawaEligible,
    alreadyUsedPromotions:
      (res.already_used_promotions &&
        res.already_used_promotions.map(promotion => ({
          promotionId: promotion.promotion_id,
          name: promotion.name,
          rejectionMessage: promotion.rejection_message
        }))) ||
      []
  }
}

export function buildCartState(res) {
  const promotions =
    res.applied_promotions &&
    Object.keys(res.applied_promotions)
      .map(el => {
        return {
          name: res.applied_promotions[el].name,
          id: el,
          priority: parseInt(res.applied_promotions[el].priority) || 0,
          noAuthFreeSampleMessage:
            res.applied_promotions[el].noAuthenticationFreeSampleMessage || null
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.priority - b.priority)

  const noAuthFreeSampleMessage =
    (promotions &&
      promotions
        .map(el => {
          return el.noAuthFreeSampleMessage
        })
        .filter(Boolean)
        .shift()) ||
    null

  let total = 0
  const products =
    res.products &&
    res.products
      .filter(el => {
        if (noAuthFreeSampleMessage && !!el.is_sample) {
          return false
        }
        return true
      })
      .map(el => {
        total += !el.is_logistics ? parseInt(el.amount, 10) : 0
        const maxQty =
          parseInt(el.max_qty, 10) > 0 ? parseInt(el.max_qty, 10) : 6
        const minQty = parseInt(el.min_qty, 10) || 1
        const availableQuantities = Array.from(Array(maxQty).keys())
          .map(q => (q + 1 >= minQty ? q + 1 : null))
          .filter(Boolean)
        const item = {
          id: el.product_id,
          itemId: el.item_id,
          name: el.product || el.variant,
          category: el.main_category_name,
          price: formatCurrency(el.amount * el.price, el.currency),
          basePrice: formatCurrency(el.amount * el.base_price, el.currency),
          maxQty,
          minQty,
          availableQuantities,
          amount: el.amount,
          hint: null,
          image:
            el.main_pair &&
            el.main_pair.icon &&
            !Array.isArray(el.main_pair.icon) &&
            typeof el.main_pair.icon === 'object'
              ? el.main_pair.icon.image_path
              : null,
          chainId: el.chain_id ? el.chain_id : null,
          chainName: el.chain_name ? el.chain_name : null,
          chainPrice: formatCurrency(el.chain_price, el.currency),
          displayDiscountValue: el.display_discount_value || false,
          isSample: !!el.is_sample,
          isLogistics: !!el.is_logistics,
          family: el.family,
          isSampleConfirmed: !!el.is_sample_confirmed,
          options: el.options.map(option => ({
            optionId: Number(option.option_id),
            variantId: Number(option.variant_id),
            color: option.hex_color,
            price: formatCurrency(option.price, el.currency),
            listPrice: formatCurrency(option.list_price, el.currency),
            stock: parseInt(option.stock, 10),
            optionName: option.option_name,
            description: option.full_description,
            shortDescription: option.short_description,
            variantName: option.variant_name,
            shortVariantName: option.short_variant_name,
            displayPostButton: option.display_post_button === 'True' || false,
            images:
              option.images &&
              option.images.map(image => ({
                path: image.image_path || null
              })),
            additionalAttributes: option.additionalAttributes
          })),
          savedOptionVariants:
            el.product_options &&
            Object.keys(el.product_options).map(key => ({
              optionId: Number(key),
              variantId: Number(el.product_options[key])
            })),
          selectedOptionIndex:
            el.selected_variant >= 0 ? el.selected_variant : 0
        }
        return item
      })

  const cartItems = []
  products.forEach(el => {
    let item = el.chainId && cartItems.find(p => p.chainId === el.chainId)
    if (item) {
      item.products.push(el)
    } else {
      item = {
        chainId: el.chainId,
        chainName: el.chainName,
        chainPrice: el.chainPrice,
        products: [el],
        loading: false
      }
      cartItems.push(item)
    }
  })
  // Sorting bundle items by family
  cartItems.forEach(cartItem =>
    cartItem.products.sort((a, b) =>
      a && a.family && b && b.family ? a.family.priority - b.family.priority : 0
    )
  )
  // Sorting items by family
  cartItems.sort((a, b) =>
    a.products[0] &&
    a.products[0].family &&
    b.products[0] &&
    b.products[0].family
      ? a.products[0].family.priority - b.products[0].family.priority
      : 0
  )
  const response = {
    cartItems: cartItems,
    totalItems: total,
    subtotal: formatCurrency(res.subtotal, res.currency),
    total: formatCurrency(res.total_without_surcharge, res.currency),
    hasDiscount: res.discount > 0,
    discount: formatCurrency(res.discount, res.currency),
    delivery: formatCurrency(res.shipping_cost, res.currency),
    discountFlat: res.discount,
    deliveryFlat: res.shipping_cost,
    hideCoupon: !res.can_apply_coupon || false,
    hasTobacco: res.has_tobacco,
    // X = user never created a TVA (redirect to TVA screen)
    // A = user created a TVA and it is approved (ask for password and proceeed with the order
    // D = user created a TVA and it is declined (redirect to TVA screen)
    // N = not determined (ask for password and proceeed with the order
    tvaStatus: res.tva_status || 'N',
    promotions: promotions,
    coupons: res.coupons ? Object.keys(res.coupons) : [],
    familyNotice: res.familyNotice,
    eligibleCoupons:
      res.eligible_coupons &&
      Object.keys(res.eligible_coupons).map(el => {
        return {
          name: res.eligible_coupons[el].name,
          code: res.eligible_coupons[el].coupon,
          expiration: res.eligible_coupons[el].expiration_date,
          description: res.eligible_coupons[el].short_description
        }
      }),
    hasFullDiscountCoupon:
      res.eligible_coupons &&
      !!(
        Object.keys(res.eligible_coupons).filter(
          el => res.eligible_coupons[el].full_discount
        ) || []
      ).length,
    promoNeedsAccountValidation: !!res.promotion_min_reg_status,
    noAuthFreeSampleMessage: noAuthFreeSampleMessage,
    sagawaEligible: res.sagawaEligible,
    alreadyUsedPromotions:
      (res.already_used_promotions &&
        res.already_used_promotions.map(promotion => ({
          promotionId: promotion.promotion_id,
          name: promotion.name,
          rejectionMessage: promotion.rejection_message
        }))) ||
      []
  }

  return response
}

export function mapCheckoutPaymentMethods(res, cart) {
  const availablePaymentMethod = {
    creditCard: !!Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].paymentType === PaymentTypes.CREDIT_CARD
    ),
    onDelivery: !!Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].paymentType === PaymentTypes.COD
    ),
    payLater: !!Object.keys(res.paymentMethods).find(
      el =>
        res.paymentMethods[el].paymentType === PaymentTypes.PAY_LATER &&
        !res.paymentMethods[el].isUnavailable
    ),
    zeroYen: !!Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].paymentType === PaymentTypes.ZERO_YEN
    ),
    payPay: !!Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].paymentType === PaymentTypes.PAY_PAY
    ),
    dBarai: !!Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].paymentType === PaymentTypes.DBARAI
    )
  }

  if (availablePaymentMethod.zeroYen) {
    const zeroYenPaymentMethod = Object.keys(res.paymentMethods)
      .map(el => res.paymentMethods[el])
      .filter(el => el.paymentType === PaymentTypes.ZERO_YEN)[0]
    return {
      paymentMethods: [zeroYenPaymentMethod],
      selectedPaymentMethodIndex: 0
    }
  }

  const adyenPaymentMethodId =
    availablePaymentMethod.creditCard &&
    Object.keys(res.paymentMethods).find(
      el => res.paymentMethods[el].processorType === 'D'
    )

  const item_order = [
    PaymentTypes.CREDIT_CARD,
    PaymentTypes.PAY_LATER,
    PaymentTypes.COD,
    PaymentTypes.PAY_PAY,
    PaymentTypes.DBARAI
  ]

  const paymentMethods = Object.keys(res.paymentMethods).map(
    el => res.paymentMethods[el]
  )
  if (!availablePaymentMethod.creditCard) {
    paymentMethods.push({ paymentType: PaymentTypes.CREDIT_CARD })
  }
  paymentMethods.sort(function (a, b) {
    const A = a.paymentType
    const B = b.paymentType
    if (item_order.indexOf(A) > item_order.indexOf(B)) {
      return 1
    } else {
      return -1
    }
  })

  const allowStoringCard = !!(
    adyenPaymentMethodId &&
    res.paymentMethods[adyenPaymentMethodId].allowStoringCard
  )

  const result = {
    paymentMethods:
      cart && cart.sagawaEligible
        ? paymentMethods.filter(i => i.paymentType !== PaymentTypes.COD)
        : paymentMethods,
    hasAdyenPaymentMethod: !!adyenPaymentMethodId,
    allowStoringCard
  }

  if (cart) {
    let selectedPaymentMethodIndex = result.paymentMethods.findIndex(
      e => e.paymentId === String(cart.paymentId)
    )
    if (selectedPaymentMethodIndex === -1) {
      selectedPaymentMethodIndex = availablePaymentMethod.creditCard
        ? result.paymentMethods.findIndex(
            e => e.paymentType === PaymentTypes.CREDIT_CARD
          )
        : availablePaymentMethod.onDelivery
        ? result.paymentMethods.findIndex(
            e => e.paymentType === PaymentTypes.COD
          )
        : 0
      if (selectedPaymentMethodIndex === -1) {
        selectedPaymentMethodIndex = 0
      }
    }
    result.selectedPaymentMethodIndex = selectedPaymentMethodIndex
  }

  return result
}

export function buildCheckoutState(res, cart) {
  const newContactInfo = {
    firstname: res.contactInfo.firstName,
    lastname: res.contactInfo.lastName,
    fullName: [res.contactInfo.lastName || '', res.contactInfo.firstName || '']
      .join(' ')
      .trim(),
    email: res.contactInfo.email,
    phone: res.contactInfo.phone
  }
  const newShippingInfo = {
    addressLine1: !res.shippingData.sAddress ? '' : res.shippingData.sAddress,
    addressLine2: '',
    city: !res.shippingData.sCity ? '' : res.shippingData.sCity,
    state: !res.shippingData.sState ? '' : res.shippingData.sState,
    postalCode: !res.shippingData.sZipCode ? '' : res.shippingData.sZipCode,
    postalCodeHidden: !res.shippingData.sZipCode
      ? ''
      : res.shippingData.sZipCode,
    country: !res.shippingData.sCountry ? '' : res.shippingData.sCountry
  }

  const deliveryOptions = buildCheckoutDeliveryOptions(
    res.shippingMethods,
    cart
  )
  const showSaveAsProfileAddress =
    res.shippingData.updateDefaultAddress === true ||
    res.shippingData.updateDefaultAddress === false

  const isDefaultAddressCheckboxDisabled =
    res.shippingData.updateDefaultAddress === false

  const shippingMethods = res.shippingMethods || []
  const yamatoIndex = shippingMethods.findIndex(
    shippingMethod => shippingMethod.shipping === ShippingTypes.YAMATO
  )
  if (yamatoIndex) {
    shippingMethods.unshift(shippingMethods.splice(yamatoIndex, 1)[0])
  }

  const paymentInfo = mapCheckoutPaymentMethods(res, cart)

  return {
    contactInfo: {
      ...newContactInfo
    },
    defaultShippingAddress: {
      ...newShippingInfo
    },
    shippingMethods: shippingMethods,
    showSaveAsProfileAddress,
    isDefaultAddressCheckboxDisabled,
    ...paymentInfo,
    ...deliveryOptions
  }
}

export function buildReplacementCheckoutState(res) {
  // TO MERGE WITH buildCheckoutState AS SOON AS REPONSES ARE ALIGNED WITH SUBFUNCTIONS FOR EACH ELEMENTS (contact info, shipping methods...)
  const contactInfo = {
    firstname: res.contactInfo.firstname,
    lastname: res.contactInfo.lastname,
    fullName: [res.contactInfo.lastname || '', res.contactInfo.firstname || '']
      .join(' ')
      .trim(),
    email: res.contactInfo.email,
    phone: res.contactInfo.phone
  }
  const shippingData = {
    addressLine1: !res.shippingData.address ? '' : res.shippingData.address,
    addressLine2: '',
    city: !res.shippingData.city ? '' : res.shippingData.city,
    state: !res.shippingData.state ? '' : res.shippingData.state,
    postalCode: !res.shippingData.zipCode ? '' : res.shippingData.zipCode,
    postalCodeHidden: !res.shippingData.zipCode ? '' : res.shippingData.zipCode,
    country: !res.shippingData.country ? '' : res.shippingData.country
  }
  const shippingMethods = buildReplacementCheckoutShippingMethods(
    res.shippingMethods
  )
  const showSaveAsProfileAddress =
    res.shippingData.updateDefaultAddress === true ||
    res.shippingData.updateDefaultAddress === false

  const isDefaultAddressCheckboxDisabled =
    res.shippingData.updateDefaultAddress === false

  return {
    contactInfo,
    shippingData,
    showSaveAsProfileAddress,
    isDefaultAddressCheckboxDisabled,
    ...shippingMethods
  }
}

export function buildCheckoutDeliveryOptions(shippingMethods, cart) {
  // TO ALIGN WITH buildReplacementCheckoutShippingMethods
  const shippingId =
    cart.products.length &&
    shippingMethods.length &&
    shippingMethods[0].shippingId
  return {
    deliveryOptions:
      cart.products.length &&
      shippingMethods &&
      shippingMethods.length &&
      shippingMethods[0].deliveryOptions &&
      shippingMethods[0].deliveryOptions.deliverySchema &&
      shippingMethods[0].deliveryOptions.deliverySchema.length > 0 &&
      shippingMethods[0].deliveryOptions.deliverySchema.map(deliveryOption => {
        return {
          day: deliveryOption.day
            ? {
                date: deliveryOption.day.date,
                leadDays: deliveryOption.day.leadDays
              }
            : null,
          timeSlots: deliveryOption.timeSlots
        }
      }),
    selectedShippingMethodIndex: shippingMethods.findIndex(
      el => String(el.shippingId) === String(shippingId)
    )
  }
}

export function buildReplacementCheckoutShippingMethods(shippingMethods) {
  // TO MERGE WITH buildCheckoutDeliveryOptions AS SOON AS RESPONSES ARE ALIGNED
  return {
    shippingMethods:
      shippingMethods &&
      shippingMethods.length &&
      shippingMethods.map(shippingMethod => {
        return {
          shippingId: shippingMethod.shippingId,
          shipping: shippingMethod.shipping,
          freeProducts: shippingMethod.freeProducts || [],
          deliveryOptions:
            shippingMethod.deliveryOptions &&
            shippingMethod.deliveryOptions.deliverySchema &&
            shippingMethod.deliveryOptions.deliverySchema.length &&
            shippingMethod.deliveryOptions.deliverySchema.map(
              deliveryOption => {
                return {
                  day: deliveryOption.day
                    ? {
                        date: deliveryOption.day.date,
                        leadDays: deliveryOption.day.leadDays
                      }
                    : null,
                  timeSlots: deliveryOption.timeSlots
                }
              }
            )
        }
      }),
    selectedShippingMethodIndex: 0
  }
}

export function buildCheckoutData(state, stepNumber) {
  const {
    shippingSetup,
    useDefaultShippingAddress,
    selectedPaymentMethodIndex,
    selectedShippingMethodIndex,
    paymentMethods,
    shippingMethods,
    toggleSaveAsProfileAddress
  } = state
  const selectedShippingMethod =
    shippingMethods &&
    shippingMethods.length > selectedShippingMethodIndex &&
    shippingMethods[selectedShippingMethodIndex]
  const shippingType = selectedShippingMethod.shipping
  const shippingId = selectedShippingMethod.shippingId
  const isPickup =
    shippingType === ShippingTypes.FSS ||
    shippingType === ShippingTypes.FSS_SIS_LOCKER ||
    shippingType === ShippingTypes.CSTORE
  const shippingMethod = { shippingId }

  const currentShippingAddress =
    isPickup || useDefaultShippingAddress
      ? state.defaultShippingAddress
      : state.newShippingAddress

  const shippingData = stepNumber &&
    stepNumber === 1 && {
      ...(toggleSaveAsProfileAddress && {
        updateDefaultAddress: toggleSaveAsProfileAddress
      }),
      zipCode: currentShippingAddress.postalCode,
      state: currentShippingAddress.state,
      city: currentShippingAddress.city,
      address:
        (currentShippingAddress.addressLine1 || '') +
        (currentShippingAddress.addressLine2 || '')
    }

  if (isPickup) {
    const { selectedPickupStore, pickupStoreList } = state
    const filteredStore =
      (selectedPickupStore &&
        pickupStoreList &&
        pickupStoreList.filter(
          store => store.ieCode === selectedPickupStore
        )) ||
      []
    const currentStore = filteredStore.length > 0 ? filteredStore[0] : null
    shippingMethod.pickupOption = {
      ieCode: currentStore && currentStore.ieCode,
      storeType: currentStore && currentStore.shopType
    }
  } else {
    shippingMethod.deliveryOption = {
      leadDays:
        (shippingSetup &&
          shippingSetup.deliverySetup &&
          shippingSetup.deliverySetup.dayId) ||
        null,
      hour:
        (shippingSetup &&
          shippingSetup.deliverySetup &&
          shippingSetup.deliverySetup.hour) ||
        0,
      label:
        (shippingSetup &&
          shippingSetup.deliverySetup &&
          shippingSetup.deliverySetup.label) ||
        '',
      warehouse:
        (shippingSetup &&
          shippingSetup.deliverySetup &&
          shippingSetup.deliverySetup.warehouse) ||
        ''
    }
  }

  const paymentId =
    (paymentMethods &&
      paymentMethods.length > selectedPaymentMethodIndex &&
      paymentMethods[selectedPaymentMethodIndex].paymentId) ||
    null

  if (stepNumber && stepNumber === 1) {
    return {
      ...(shippingData && { shippingData }),
      ...(shippingMethod && { shippingMethod })
    }
  }
  return {
    ...(paymentId && { paymentId })
  }
}

export function buildReplacementCheckoutData(
  selectedShippingMethodIndex,
  toggleSaveAsProfileAddress,
  useDefaultShippingAddress,
  newShippingAddress,
  selectedPickupStore,
  pickupStoreList,
  currentDelivery,
  currentDeliveryTimeSlot,
  deliveryMethods,
  defaultDeliveryAddress
) {
  const selectedShippingMethod =
    deliveryMethods &&
    deliveryMethods.length > selectedShippingMethodIndex &&
    deliveryMethods[selectedShippingMethodIndex]
  const shippingType = selectedShippingMethod.shipping
  const shippingId = selectedShippingMethod.shippingId
  const isPickup =
    shippingType === ShippingTypes.FSS || shippingType === ShippingTypes.CSTORE
  const shippingMethod = { shippingId }

  const currentShippingAddress =
    isPickup || useDefaultShippingAddress
      ? defaultDeliveryAddress
      : newShippingAddress

  const shippingData = {
    ...(toggleSaveAsProfileAddress && {
      updateDefaultAddress: toggleSaveAsProfileAddress
    }),
    zipCode: currentShippingAddress.postalCode,
    state: currentShippingAddress.state,
    city: currentShippingAddress.city,
    address:
      (currentShippingAddress.addressLine1 || '') +
      (currentShippingAddress.addressLine2 || '')
  }

  if (isPickup) {
    const filteredStore =
      (selectedPickupStore &&
        pickupStoreList &&
        pickupStoreList.filter(
          store => store.ieCode === selectedPickupStore
        )) ||
      []
    const currentStore = filteredStore.length > 0 ? filteredStore[0] : null
    shippingMethod.pickupOption = {
      ieCode: currentStore && currentStore.ieCode,
      storeType: currentStore && currentStore.shopType
    }
  } else {
    shippingMethod.deliveryOption = {
      leadDays:
        (currentDelivery &&
          currentDelivery.day &&
          currentDelivery.day.leadDays) ||
        null,
      hour: (currentDeliveryTimeSlot && currentDeliveryTimeSlot.hour) || 0,
      label: (currentDeliveryTimeSlot && currentDeliveryTimeSlot.label) || '',
      warehouse:
        (currentDeliveryTimeSlot && currentDeliveryTimeSlot.warehouse) || ''
    }
  }
  return {
    ...(shippingData && { shippingData }),
    ...(shippingMethod && { shippingMethod })
  }
}

export function isAuthenticated() {
  return (
    canUseDOM &&
    !!(window && window.user_information && window.user_information.logged_id)
  )
}

const lilDeviceFamily = 'lil'

export function mapDeviceListForDisplay(res, groupByDeviceType = false) {
  const allDevices = (res.devices || []).map(device => ({
    id: device.id,
    type: device.type,
    family: device.family,
    version: device.version,
    image: device.component && device.component.image,
    altImage:
      device.component &&
      getImageAltValue(device.component.alt, device.component.name),
    name: device.component && device.component.name,
    purchased: device.purchasedOn ? device.purchasedOn : null,
    registered: device.registeredOn,
    codentify: device.codentifyId,
    qureEnabled: device.qure_enabled,
    qureEnabledReason: device.qure_enabled_reason,
    qureUrl: device.qure_url,
    chatBotEnabled: device.chatbot_enabled,
    chatBotUrl: device.chatbot_url,
    deviceActions: device.deviceActions,
    dvcCode: device.dvc_code,
    registeredStatus: device.registrationStatus,
    warrantyStatus: device.warrantyStatus,
    warrantyEnd: device.warrantyEnd,
    replacementStatus: device.replacementStatus,
    defectedCode: device.subject_code,
    isDiagnosticDisabled: device.isDiagnosticDisabled,
    isUpgraded: !!(
      device.related_status &&
      device.related_status.toUpperCase() === 'UPGRADE PURCHASE'
    ),
    relatedCodentifyId: device.related_codentifyId
  }))

  if (groupByDeviceType) {
    const devices = []
    const devicesLIL = []
    allDevices.forEach(device => {
      if (device.family && device.family.toLowerCase() === lilDeviceFamily) {
        devicesLIL.push(device)
      } else {
        devices.push(device)
      }
    })
    return {
      devices: devices,
      devicesLIL: devicesLIL,
      showClaimDriBanner: res.show_claim_dri_banner
    }
  }

  return {
    devices: allDevices,
    showClaimDriBanner: res.show_claim_dri_banner
  }
}

export function getToastMessagesFromResponse(res, successTitle, errorTitle) {
  const messages = [
    res.error ? errorTitle : successTitle,
    res.error,
    res.warning,
    res.notice
  ]
  const result = messages.filter(Boolean)
  return result
}

export function getToastMessagesFromError(
  err,
  errorTitle,
  errorDefaultMessage
) {
  const messages = [
    ...new Set([errorTitle, extractErrorMessage(err, errorDefaultMessage)])
  ]
  const result = messages.filter(Boolean)
  return result
}

const sanitizeHtml = s => striptags(s)

export function formattedDescriptionJSONLD(description) {
  const sanitize = sanitizeHtml(description)
  const index = sanitize.indexOf('\r\n\r\n')
  const length = index === -1 ? 160 : Math.min(index, 160)
  return sanitize.replace(/(\r\n|\n|\r)/gm, '').substr(0, length)
}

export const getCurrentURL = (params = '') => {
  return (
    canUseDOM &&
    qs.stringify({
      callredirectUrl:
        window.location.pathname +
        (window.location.search || '') +
        (window.location.hash || '') +
        params
    })
  )
}

export const getImageAltValue = (altValue, productName) => {
  return altValue || `${productName} ${constants.IMAGE_ALT_DEFAULT_VALUE}`
}

export const extractGigyaErrorCode = (message = '') => {
  const result = message.match(/Gigya Error : ([0-9]*.) :/) || []
  return result[1] || null
}

export function mapOrderListForDisplay(orders) {
  return (orders || []).map(order => {
    let shipping = ''
    if (order.shipping_ids && order.shippings) {
      shipping = order.shippings[order.shipping_ids]
    }
    const products =
      order.products &&
      order.products.map(el => {
        return {
          ...el,
          price: formatCurrency(el.price, el.currency),
          chainId: el.chain_id ? el.chain_id : null,
          chainName: el.chain_description ? el.chain_description : null,
          chainPrice: formatCurrency(el.chain_price, el.currency)
        }
      })

    const cartItems = []
    products &&
      products.forEach(el => {
        let item = el.chainId && cartItems.find(p => p.chainId === el.chainId)
        if (item) {
          item.products.push(el)
        } else {
          item = {
            chainId: el.chainId,
            chainName: el.chainName,
            chainPrice: el.chainPrice,
            products: [el]
          }
          cartItems.push(item)
        }
      })

    return {
      id: order.order_id,
      ordinal: order.timestamp * 1000,
      number: order.order_id,
      total: formatCurrency(order.total, order.currency),
      status: order.status, // missing description for status type (N, C, P)
      delivery: order.delivery_data ? order.delivery_data.delivery_name : null,
      shippingAddress: order.shippingAddress ? order.shippingAddress : null,
      shipping: shipping,
      shipments: order.shipments,
      shippingStatus: order.shipping_status_description,
      detail: {
        cartItems: cartItems,
        addresses: []
      },
      isCancellable: order.is_cancellable
    }
  })
}

export const buildOrderAdditionalData = (useCamelCase = false) => {
  const $adnFingerPrint = document.getElementById('adn_df')
  return useCamelCase
    ? {
        deviceFingerprint: $adnFingerPrint && $adnFingerPrint.value,
        browserInfo: canUseDOM
          ? {
              colorDepth: window.screen.colorDepth,
              javaEnabled: window.navigator.javaEnabled(),
              language: window.navigator.language,
              screenHeight: window.screen.height,
              screenWidth: window.screen.width,
              timeZoneOffset: new Date().getTimezoneOffset()
            }
          : null
      }
    : {
        DeviceFingerprint: $adnFingerPrint && $adnFingerPrint.value,
        BrowserInfo: canUseDOM
          ? {
              ColorDepth: window.screen.colorDepth,
              JavaEnabled: window.navigator.javaEnabled(),
              Language: window.navigator.language,
              ScreenHeight: window.screen.height,
              ScreenWidth: window.screen.width,
              TimeZoneOffset: new Date().getTimezoneOffset()
            }
          : null
      }
}

export const checkStockNotification = (
  product,
  selectedOptionIndex,
  stockNotification
) => {
  if (stockNotification.products && stockNotification.products.length > 0) {
    const selectedOption = product.options[selectedOptionIndex]
    const item = stockNotification.products.find(
      p => String(p.id) === String(product.id)
    )
    const variant =
      item &&
      item.options.find(
        p =>
          String(p.optionId) === String(selectedOption.optionId) &&
          String(p.variantId) === String(selectedOption.variantId)
      )
    return !!variant
  }
  return false
}

export const buildDebugTranslations = (
  component,
  translations,
  translationsConfig,
  showDefault = false,
  showHtml = false
) => {
  const comp =
    document.getElementById(component) ||
    document.querySelector('[data-component="' + component + '"]')
  if (comp && !document.getElementById('debug-comp-title-' + component)) {
    comp.insertAdjacentHTML(
      'afterbegin',
      `<div id="debug-comp-title-${component}">[Component: ${component}]</div>`
    )
    comp.style.border = 'solid'
  }
  console.log('Show Defaults: ', showDefault, 'Show Htmls: ', showHtml)
  console.log(component + ' translations:', { ...translations })
  console.log(component + ' translations configuration:', {
    ...translationsConfig
  })
  const debugTranslations = { ...translations }
  Object.keys(translationsConfig).forEach(key => {
    debugTranslations[key] =
      '[Key: ' +
      key +
      (showDefault ? ' | Default: ' + translationsConfig[key].default : '') +
      (showHtml ? ' | Html: ' + translationsConfig[key].html : '') +
      '] ' +
      translations[key]
  })
  return debugTranslations
}

export const isCartPurchaseLimitationsErrorCode = errCode => {
  return (
    errCode === PurchaseLimitationErrorCodes.TRANSACTION_AMOUNT ||
    errCode === PurchaseLimitationErrorCodes.ISOLATED_ITEM
  )
}

export const isHistoryPurchaseLimitationsErrorCode = errCode => {
  return (
    errCode === PurchaseLimitationErrorCodes.REGISTERED_DEVICE_AMOUNT ||
    errCode === PurchaseLimitationErrorCodes.CUSTOMER_OWNING_AMOUNT ||
    errCode === PurchaseLimitationErrorCodes.GLOBAL_AMOUNT ||
    errCode ===
      PurchaseLimitationErrorCodes.OVER_PURCHASE_LIMIT_INCOMPLETE_ORDER ||
    errCode ===
      PurchaseLimitationErrorCodes.REACHED_LIMIT_PER_DEVICE_INCOMPLETE_ORDER
  )
}

export const isPurchaseLimitationsErrorCode = errCode => {
  return (
    isCartPurchaseLimitationsErrorCode(errCode) ||
    isHistoryPurchaseLimitationsErrorCode(errCode)
  )
}

export const logUnavailablePaylaterError = payLaterAttributes => {
  const errors = []
  if (!payLaterAttributes.LNDPIC) {
    errors.push('LNDPIC is not being populated correctly during Paylater setup')
  }
  if (!payLaterAttributes.paylaterLaunch) {
    errors.push('Paylater was not able to launch itself correctly')
  }
  if (!payLaterAttributes.paylaterApi) {
    errors.push('Paylater api setup is missing')
  }
  if (!payLaterAttributes.paylaterFrame) {
    errors.push('Paylater frame is not being created correctly')
  }
  newRelicReport(
    new Error(
      `Paylater setup error on order (${
        payLaterAttributes.order
      }): ${errors.join('. ')}`
    )
  )
}

export const getDateInTimeZone = date => {
  return new Date(
    new Date(date).toLocaleString('en', { timeZone: 'Asia/Tokyo' })
  )
}

export const renderFormattedDate = date => {
  return format(getDateInTimeZone(date), constants.DEFAULT_DATE_FORMAT)
}

export const listenEvent = (eventName, callback, kill) => {
  if (canUseDOM) {
    kill
      ? window && window.removeEventListener(eventName, callback)
      : window && window.addEventListener(eventName, callback)
  }
}

export const loadExternalScript = (id, url, onLoadCallback) => {
  if (!canUseDOM) return
  const existingScript = document.getElementById(id)
  if (!existingScript) {
    const script = document.createElement('script')
    script.src = url
    script.id = id
    document.body.appendChild(script)
    script.onload = () => {
      onLoadCallback && onLoadCallback()
    }
  }
  existingScript && onLoadCallback && onLoadCallback()
}

export const checkLoginWarnings = (
  localstorage,
  analytics,
  translations,
  popupToast,
  ToastContext
) => {
  const loginWarning = localstorage.get('LoginWarning') || null
  localstorage.remove('LoginWarning')
  const cartMigrationErrors = localstorage.get('CartMigrationErrors') || null
  localstorage.remove('CartMigrationErrors')
  if (cartMigrationErrors && cartMigrationErrors.length > 0) {
    let hasPLError = false
    let toastContent = ''
    cartMigrationErrors.forEach(error => {
      if (isPurchaseLimitationsErrorCode(error.code)) {
        hasPLError = true
        analytics.addToCartLimitations(
          error.variantId,
          error.variantName,
          isCartPurchaseLimitationsErrorCode(error.code)
        )
      }
      if (error.description) {
        toastContent = `${toastContent ? toastContent + '<br>' : ''}${
          error.variantName ? error.variantName + ' - ' : ''
        }${error.description}`
      }
    })
    toastContent = hasPLError
      ? `${toastContent}<br>${translations.tk_purchaseLimitationsErrorTermsAndConditions}`
      : toastContent
    if (toastContent) {
      popupToast({
        type: ToastContext.types.error,
        title: hasPLError
          ? translations.tk_purchaseLimitationsErrorToastTitle
          : null,
        subtitle: toastContent
      })
    }
    return
  }
  if (loginWarning && loginWarning.length > 0) {
    popupToast({
      type: ToastContext.types.none,
      subtitle: loginWarning
    })
  }
}

export const parseTranslations = (translations, defaultTrans) => {
  const resultRes = {}
  const keys = Object.keys(translations)
  Object.values(translations).forEach((value, index) => {
    if (value) {
      resultRes[keys[index]] = !defaultTrans ? value : value.default
      if (value.html) {
        resultRes._html = { ...resultRes._html, [keys[index]]: true }
      }
    }
  })
  if (resultRes._html) {
    resultRes._html = JSON.stringify(resultRes._html)
  }
  return resultRes
}

export const htmlTranslation = (translations, key) => {
  const htmlConfig = translations._html ? JSON.parse(translations._html) : {}
  if (translations[key]) {
    return {
      value: translations[key],
      acceptsHtml: htmlConfig && !!htmlConfig[key]
    }
  }
}

export const resetGrecaptcha = action => {
  canUseDOM &&
    window.dispatchEvent(
      new window.CustomEvent('resetGrecaptcha', { detail: action })
    )
}

export const checkExternalCampaign = (token, localstorage, api) => {
  const ecc = localstorage.get('ECC')
  localstorage.remove('ECC')
  if (ecc) {
    return api.cart.externalCampaign(token, ecc)
  }
  return null
}

export const isProductEligibleToShow = (product, cookie) => {
  if (canUseDOM) {
    const token = cookie.getToken()
    const decodedToken = JWTDecode(token)
    if (
      product &&
      product.isRequiredDeviceRegistration &&
      (!isAuthenticated() ||
        (decodedToken && decodedToken.has_device_registered === 'false'))
    ) {
      return false
    }
    return true
  }
  return true
}

export const dispatchPopupPromotionAlreadyUsed = (
  spiceId,
  data,
  popupToast,
  translations,
  Container,
  TextBody
) => {
  const key = `${constants.DCX_PROMOTIONS_NOTIFICATIONS_SEEN}__${spiceId}`
  const sessionkey = JSON.parse(canUseDOM && window.sessionStorage.getItem(key))
  let dispatchPopup = false

  const idMap = new Map()
  const promotionsArray = []

  if (Array.isArray(data)) {
    data.forEach(item => {
      // product-list flows
      const selectedOption = item.selectedOptionIndex || 0
      const promotions =
        item.options[selectedOption].alreadyUsedPromotions || []
      promotions.forEach(promotion => {
        const { promotionId, name, rejectionMessage } = promotion
        const promotionObject = { promotionId, name, rejectionMessage }
        if (!idMap.has(promotionId)) {
          idMap.set(promotionId, true)
          promotionsArray.push(promotionObject)
        }
      })
    })
  } else {
    let promotions = []
    if (data.alreadyUsedPromotions) {
      // cart and checkout
      promotions = data.alreadyUsedPromotions || []
    } else {
      // product-detail and product-list flows
      const selectedOption = data.selectedOptionIndex
      promotions =
        data.product.options[selectedOption].alreadyUsedPromotions || []
    }
    promotions.forEach(promotion => {
      const { promotionId, name, rejectionMessage } = promotion
      const promotionObject = { promotionId, name, rejectionMessage }
      if (!idMap.has(promotionId)) {
        idMap.set(promotionId, true)
        promotionsArray.push(promotionObject)
      }
    })
  }

  let listOfPromotions = promotionsArray

  if (sessionkey && sessionkey.length > 0) {
    // session storage list exists
    // promotions = list of promotions not setted on session storage list
    const promotions = promotionsArray.filter(
      obj => !sessionkey.includes(obj.promotionId)
    )

    if (promotions.length !== 0) {
      // adding promotionIDs that DO NOT exists on session storage list
      // show popup
      dispatchPopup = true
      listOfPromotions = promotions
      sessionkey.push(...promotions.map(p => p.promotionId))
      window.sessionStorage.setItem(key, JSON.stringify(sessionkey))
    }
  } else {
    // session storage list do not exist
    // set new session storage with promotionIDs in array
    // show popup
    if (promotionsArray && promotionsArray.length > 0) {
      dispatchPopup = true
      window.sessionStorage.setItem(
        key,
        JSON.stringify(
          promotionsArray.map(promotion => promotion.promotionId) || []
        )
      )
    }
  }

  if (dispatchPopup) {
    popupToast({
      autoDismiss: false,
      title: translations.tk_promotionsNotUsedToastTitle,
      content: listOfPromotions.map((promotion, key) => (
        <Container
          key={key}
          display={Container.displays.flex}
          alignItems={Container.alignItemsValues.center}
          flexDirection={Container.flexDirections.row}>
          <TextBody size={TextBody.sizes.s}>{promotion.name}</TextBody>:
          <TextBody size={TextBody.sizes.s}>
            {promotion.rejectionMessage}
          </TextBody>
        </Container>
      ))
    })
  }
}

export const getBase64FromImg = async (imageFile, cb, errorCb) => {
  try {
    Resizer.imageFileResizer(imageFile, 1920, 1080, 'JPEG', 90, 0, cb, 'base64')
  } catch (err) {
    errorCb && errorCb(err)
  }
}
